import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Eye } from 'lucide-react';
import { ETHIOPIAN_CITIES } from '@/lib/ethiopianCities';
import { BannerSlot } from '@/components/banners/BannerSlot';

const PAGE_OPTIONS = [
  'homepage', 'buy', 'rent', 'commercial', 'plots', 'projects',
  'agencies', 'agents', 'wanted', 'blog', 'news',
];

export default function BannerPreview() {
  const today = new Date().toISOString().slice(0, 10);
  const [placementId, setPlacementId] = useState<string>('');
  const [page, setPage] = useState<string>('homepage');
  const [city, setCity] = useState<string>('any');
  const [date, setDate] = useState<string>(today);

  const { data: placements } = useQuery({
    queryKey: ['banner-placements-preview'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('banner_placements' as any)
        .select('id,code,name,width_px,height_px,available_spots,pages')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return (data as any[]) || [];
    },
  });

  const selected = useMemo(
    () => placements?.find((p) => p.id === placementId),
    [placements, placementId]
  );

  const cityOverride = city === 'any' ? '' : city;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold flex items-center gap-2">
          <Eye className="h-6 w-6 text-primary" /> Banner Preview
        </h1>
        <p className="text-sm text-muted-foreground">
          Pick a placement, page, city and date to see exactly which banner would render.
          Includes paused / scheduled banners; auto-expired and archived ones are hidden.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
          <CardDescription>Impressions and clicks are NOT logged in preview mode.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-1.5">
            <Label>Placement</Label>
            <Select value={placementId} onValueChange={setPlacementId}>
              <SelectTrigger><SelectValue placeholder="Pick a placement…" /></SelectTrigger>
              <SelectContent>
                {placements?.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} {p.width_px ? `· ${p.width_px}×${p.height_px}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Page</Label>
            <Select value={page} onValueChange={setPage}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(selected?.pages?.length ? selected.pages : PAGE_OPTIONS).map((pg: string) => (
                  <SelectItem key={pg} value={pg}>{pg}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>City</Label>
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any (no city targeting)</SelectItem>
                {ETHIOPIAN_CITIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Preview</CardTitle>
            <CardDescription>
              {selected
                ? <>Showing up to {selected.available_spots} spot(s) at {selected.width_px || '?'}×{selected.height_px || '?'} px</>
                : 'Pick a placement to start.'}
            </CardDescription>
          </div>
          <div className="flex gap-2 text-xs">
            <Badge variant="outline">page: {page}</Badge>
            <Badge variant="outline">city: {cityOverride || 'any'}</Badge>
            <Badge variant="outline">date: {date}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {placementId ? (
            <div className="bg-muted/30 rounded-lg p-4 border border-dashed">
              <BannerSlot
                placementId={placementId}
                page={page}
                cityOverride={cityOverride}
                dateOverride={date}
                previewMode
                layout="row"
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No placement selected.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
